/**
 * P2P Contacts Panel Component
 *
 * Displays the user's real contacts, and the demo contacts (Wikipedia Alice & Bob
 * characters) only when demo mode is explicitly enabled.
 *
 * Visibility rules:
 * - Show when demo mode is enabled OR real contacts exist
 * - Demo contacts render only in demo mode (never merely because of the network)
 * - Non-production builds may show a toggle to flip demo mode
 *
 * @see docs/DemoGuide.md#p2p-contacts-demo-testnet-only
 * @see docs/UI_WIREFRAMES.md#p2p-contacts-demo-testnet-scope
 */

import React, { useState, useEffect } from "react";
import { DEMO_CONTACTS, type DemoContact } from "../../../data/demoContacts";
import type { BitcoinNetwork } from "../../../types";

export interface P2PContactsPanelProps {
  /** Current Bitcoin network */
  bitcoinNetwork?: BitcoinNetwork;
  /** Demo mode enabled (from siteMetadata.demoMode.enabled) */
  demoModeEnabled?: boolean;
  /** Show demo toggle (from siteMetadata.demoMode.showToggle) */
  showDemoToggle?: boolean;
  /** Callback when demo mode is toggled */
  onDemoModeToggle?: (enabled: boolean) => void;
  /** Real contacts (if any exist, panel shows regardless of mode) */
  realContacts?: Array<{
    id: string;
    name: string;
    address?: string;
  }>;
  /** Whether panel starts expanded (for navigation from sidebar "View all") */
  defaultExpanded?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Check if current network is a testnet variant
 */
function isTestnet(network?: BitcoinNetwork): boolean {
  if (!network) return false;
  return network !== "mainnet";
}

export const P2PContactsPanel: React.FC<P2PContactsPanelProps> = ({
  bitcoinNetwork,
  demoModeEnabled = false,
  showDemoToggle = false,
  onDemoModeToggle,
  realContacts = [],
  defaultExpanded = false,
  className = "",
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [localDemoMode, setLocalDemoMode] = useState(demoModeEnabled);

  // Sync expanded state when defaultExpanded prop changes (e.g., navigating from sidebar)
  useEffect(() => {
    if (defaultExpanded) {
      setIsExpanded(true);
    }
  }, [defaultExpanded]);

  const effectiveDemoMode = localDemoMode || demoModeEnabled;
  const onTestnet = isTestnet(bitcoinNetwork);
  const hasRealContacts = realContacts.length > 0;

  // Determine visibility.
  // Show real contacts whenever they exist; show demo contacts only when demo
  // mode is explicitly enabled. The network alone never surfaces demo contacts.
  const shouldShow = effectiveDemoMode || hasRealContacts;

  // Demo contacts are shown only in demo mode with no real contacts.
  const isDemoContacts = !hasRealContacts && effectiveDemoMode;

  if (!shouldShow) {
    return null;
  }

  const handleToggle = () => {
    const newValue = !localDemoMode;
    setLocalDemoMode(newValue);
    onDemoModeToggle?.(newValue);
  };

  return (
    <div
      className={`border border-sa-border rounded-lg bg-sa-surface-hover ${className}`}
      data-testid="p2p-contacts-panel"
    >
      {/* Header */}
      <button
        type="button"
        className="w-full p-3 flex items-center justify-between"
        onClick={() => setIsExpanded(!isExpanded)}
        data-testid="p2p-contacts-toggle"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
            <svg
              className="w-4 h-4 text-primary"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <div className="text-left">
            <p className="text-sm font-medium text-sa-text">P2P Contacts</p>
            <p className="text-xs text-sa-text-muted">
              {isDemoContacts ? "Demo Contacts" : `${realContacts.length} contacts`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isDemoContacts && (
            <span className="text-[10px] bg-sa-warning/10 text-sa-warning px-1.5 py-0.5 rounded-md">
              {onTestnet ? "Testnet" : "Demo"}
            </span>
          )}
          <svg
            className={`w-4 h-4 text-sa-text-muted transition-transform ${isExpanded ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-2">
          {/* Demo toggle (non-production only) */}
          {showDemoToggle && (
            <div
              className="flex items-center justify-between py-2 px-2 bg-sa-surface-hover rounded text-xs"
              data-testid="p2p-demo-toggle"
            >
              <span className="text-sa-text-muted">Show demo contacts</span>
              <button
                type="button"
                onClick={handleToggle}
                className={`w-10 h-5 rounded-full transition-colors ${
                  localDemoMode ? "bg-primary" : "bg-sa-border"
                }`}
              >
                <span
                  className={`block w-4 h-4 bg-sa-surface rounded-full shadow transform transition-transform ${
                    localDemoMode ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
          )}

          {/* Contacts list */}
          <div className="max-h-64 overflow-y-auto space-y-1">
            {(isDemoContacts ? DEMO_CONTACTS : realContacts).map((contact) => (
              <ContactRow key={contact.id} contact={contact} isDemoContact={isDemoContacts} />
            ))}
          </div>

          {/* Footer note for demo contacts */}
          {isDemoContacts && (
            <p className="text-[10px] text-sa-text-muted pt-2 border-t border-sa-border">
              Demo contacts from cryptographic protocol standards. Real contacts will appear when
              added.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Individual contact row component
 */
interface ContactRowProps {
  contact: DemoContact | { id: string; name: string; address?: string };
  isDemoContact: boolean;
}

const ContactRow: React.FC<ContactRowProps> = ({ contact, isDemoContact }) => {
  const demoContact = isDemoContact ? (contact as DemoContact) : null;

  return (
    <div
      className="flex items-center justify-between p-2 rounded hover:bg-sa-surface-hover transition-colors"
      data-testid={`p2p-contact-${contact.id}`}
    >
      <div className="flex items-center gap-2">
        {/* Avatar */}
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center text-white text-xs font-medium">
          {contact.name.charAt(0)}
        </div>
        <div>
          <p className="text-sm font-medium text-sa-text">{contact.name}</p>
          {demoContact && <p className="text-[10px] text-sa-text-muted">{demoContact.role}</p>}
        </div>
      </div>
      {/* Status indicator */}
      {isDemoContact ? (
        <span className="text-[10px] text-sa-text-dim">Demo</span>
      ) : (
        <div className="w-2 h-2 rounded-full bg-green-500" title="Online" />
      )}
    </div>
  );
};

export default P2PContactsPanel;
